import React from 'react';
import { Page, Text, View, Document, StyleSheet, Image } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontFamily: 'Helvetica',
    lineHeight: 1.5,
    backgroundColor: '#ffffff',
  },
  // Encabezado tipo Sol Travel
  headerContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
    borderBottomWidth: 2,
    borderBottomColor: '#ff9f43',
    paddingBottom: 10,
  },
  headerLeft: {
    flexDirection: 'column',
  },
  logo: {
    width: 150,
    height: 90,
    objectFit: 'contain',
  },
  agencyName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#000',
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  agencySub: {
    fontSize: 12,
    color: '#555',
    marginTop: 2,
  },
  mainTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    textAlign: 'center',
    marginVertical: 15,
    textTransform: 'uppercase',
    textDecoration: 'underline',
    color: '#333',
  },
  dateInfo: {
    fontSize: 12,
    marginBottom: 20,
    fontWeight: 'bold',
    textAlign: 'center',
    color: '#666',
  },
  // Bloques de Opciones
  optionBlock: {
    marginBottom: 25,
    borderWidth: 1,
    borderColor: '#eee',
    borderRadius: 4,
    padding: 10,
  },
  optionHeader: {
    fontSize: 13,
    fontWeight: 'bold',
    backgroundColor: '#f8f9fa',
    padding: 6,
    marginBottom: 8,
    color: '#e67e22',
  },
  includesTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    textDecoration: 'underline',
    marginTop: 5,
    marginBottom: 2,
  },
  includeItem: {
    fontSize: 12,
    marginLeft: 10,
    marginBottom: 2,
    color: '#444',
  },
  // Precios de Alojamiento
  accRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 5,
    paddingLeft: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
    paddingBottom: 2,
  },
  accName: {
    fontSize: 12,
    fontWeight: 'bold',
    width: '70%',
  },
  accPrice: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#28a745',
  },
  // Segunda Página - Estilo Galería
  hotelGalleryItem: {
    marginBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#ccc',
    paddingBottom: 10,
  },
  hotelImage: {
    width: '100%',
    height: 150,
    marginBottom: 10,
    objectFit: 'cover',
    backgroundColor: '#eee', // Color de fondo si no carga imagen
  },
  // Footer Legal
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 40,
    right: 40,
    borderTopWidth: 1,
    borderTopColor: '#ccc',
    paddingTop: 10,
  },
  legalText: {
    fontSize: 10,
    color: '#777',
    textAlign: 'left',
    marginBottom: 2,
  },
  validity: {
    fontSize: 9,
    fontWeight: 'bold',
    marginTop: 5,
    color: '#d35400',
  },
  // Estilos de Tabla Itinerario
  table: {
    width: '100%',
    borderWidth: 1,
    borderColor: '#e9ecef',
    marginBottom: 20,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e9ecef',
    alignItems: 'center',
    minHeight: 24,
  },
  tableHeader: {
    backgroundColor: '#f8f9fa',
  },
  tableCell: {
    flex: 1,
    fontSize: 12,
    padding: 5,
    color: '#444',
    textAlign: 'center',
  },
  tableCellLeft: {
    flex: 2, // Más espacio para el nombre del destino
    fontSize: 12,
    padding: 5,
    color: '#444',
    textAlign: 'left',
  },
  tableHeaderCell: {
    flex: 1,
    fontSize: 12,
    fontWeight: 'bold',
    color: '#495057',
    padding: 5,
    textAlign: 'center',
  },
  tableHeaderCellLeft: {
    flex: 2,
    fontSize: 12,
    fontWeight: 'bold',
    color: '#495057',
    padding: 5,
    textAlign: 'left',
  },
  destinationImagesContainer: {
    marginTop: 20,
    marginBottom: 10,
  },
  destinationImage: {
    width: '100%',
    height: 200,
    objectFit: 'cover',
    marginBottom: 15,
    backgroundColor: '#eee',
  }
});

// Callback para evitar que las palabras se corten al final de una línea
const noHyphenationCallback = word => [word];

const formatDate = (dateString) => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  return `${day}/${month}/${year}`;
};

const Header = () => (
  <View style={styles.headerContainer} fixed>
    <View style={styles.headerLeft}>
      <Text style={styles.agencyName}>AGENCIA DE TURISMO SOL TRAVEL</Text>
      <Text style={styles.agencySub}>CASA CENTRAL: 2614717264 - 2616094641</Text>
      <Text style={styles.agencySub}>SARMIENTO 93, ESQ SANTA MARIA DE ORO, LUJAN DE CUYO</Text>
    </View>
    <Image style={styles.logo} src="/images/logo.png" />
  </View>
);

const BudgetDocument = ({ budget }) => {
  // Ordenar destinos cronológicamente
  const sortedDestinations = budget.budgetDestinations 
    ? [...budget.budgetDestinations].sort((a, b) => new Date(a.departureDate) - new Date(b.departureDate))
    : [];

  // 1. Obtener destinos únicos
  const uniqueDestinations = [...new Set(
    sortedDestinations
      ?.map(bd => bd.destination?.destinationName)
      .filter(Boolean)
  )].join(' - ');

  // Obtener objetos de destino únicos para las imágenes
  const uniqueDestinationObjects = sortedDestinations
    ? Array.from(new Map(sortedDestinations.map(bd => [bd.destination.id, bd.destination])).values())
    : [];

  // 2. Obtener mes del viaje (basado en la primera fecha de salida)
  let monthString = '';
  if (sortedDestinations?.[0]?.departureDate) {
    const months = ["ENERO", "FEBRERO", "MARZO", "ABRIL", "MAYO", "JUNIO", "JULIO", "AGOSTO", "SEPTIEMBRE", "OCTUBRE", "NOVIEMBRE", "DICIEMBRE"];
    const parts = sortedDestinations[0].departureDate.split('-'); // Asume formato YYYY-MM-DD
    if (parts.length === 3) monthString = ` ${months[parseInt(parts[1], 10) - 1]}`;
  }

  return (
  <Document title={`Presupuesto_${budget.passenger?.surname || 'Cliente'}`}>
    {/* PÁGINA 1: DATOS Y COSTOS */}
    <Page size="A4" style={styles.page} hyphenationCallback={noHyphenationCallback}>
      <Header />

      <Text style={styles.mainTitle} hyphenationCallback={noHyphenationCallback}>
        PRESUPUESTO {uniqueDestinations || 'DE VIAJE'}{monthString}
      </Text>

      <Text style={styles.dateInfo}>
        Fecha: {formatDate(sortedDestinations?.[0]?.departureDate) || 'A definir'} al {formatDate(sortedDestinations?.[sortedDestinations.length - 1]?.returnDate) || 'A definir'}
      </Text>

      {/* IMÁGENES DE DESTINOS */}
      {uniqueDestinationObjects.length > 0 && (
        <View style={styles.destinationImagesContainer} wrap={false}>
          {uniqueDestinationObjects.map(dest => (
            dest && dest.imageUrl && (
              <Image
                key={dest.id}
                style={styles.destinationImage}
                src={dest.imageUrl}
              />
            )
          ))}
        </View>
      )}

      {/* TABLA DE ITINERARIO DETALLADA */}
      {sortedDestinations && sortedDestinations.length > 0 && (
        <View style={styles.table}>
          <View style={[styles.tableRow, styles.tableHeader]}>
            <Text style={styles.tableHeaderCellLeft}>Destino</Text>
            <Text style={styles.tableHeaderCell}>Salida</Text>
            <Text style={styles.tableHeaderCell}>Regreso</Text>
            <Text style={styles.tableHeaderCell}>Noches</Text>
          </View>
          {sortedDestinations.map((bd, index) => {
             const nights = (bd.departureDate && bd.returnDate) ? 
               Math.ceil(Math.abs(new Date(bd.returnDate) - new Date(bd.departureDate)) / (1000 * 60 * 60 * 24)) : 0;
             return (
               <View key={index} style={styles.tableRow}>
                 <Text style={styles.tableCellLeft}>{bd.destination ? `${bd.destination.destinationName} - ${bd.destination.country}` : 'N/A'}</Text>
                 <Text style={styles.tableCell}>{formatDate(bd.departureDate)}</Text>
                 <Text style={styles.tableCell}>{formatDate(bd.returnDate)}</Text>
                 <Text style={styles.tableCell}>{nights}</Text>
               </View>
             );
          })}
        </View>
      )}

      {budget.budgetOptions?.map((opt, index) => (
        <View key={index} style={styles.optionBlock}>
          <Text style={styles.optionHeader}>OPCIÓN {index + 1}: Salida desde {opt.origin || '...'}</Text>
          
          <Text style={styles.includesTitle}>INCLUYE:</Text>
          <Text style={styles.includeItem}>• {opt.typeTrip} ida y vuelta desde {opt.origin} con {opt.travelCompany} ({opt.luggage}).</Text>
          {opt.transfer && <Text style={styles.includeItem}>• Traslado : {opt.transferDescription || 'In/Out Aeropuerto - Hotel'}</Text>}
          {opt.carRental && <Text style={styles.includeItem}>• Alquiler de Auto: {opt.carRentalDescription}</Text>}
          {opt.excursion && <Text style={styles.includeItem}>• Excursiones: {opt.excursionDescription}</Text>}
          {opt.travelInsurance && <Text style={styles.includeItem}>• Seguro de Viaje: {opt.travelInsuranceDescription}</Text>}
          {opt.taxes && <Text style={styles.includeItem}>• Impuestos incluidos.</Text>}
          
          {opt.accommodations && opt.accommodations.length > 0 && (
            <>
              <Text style={[styles.includesTitle, { marginTop: 10 }]}>• Alojamiento:</Text>
              {opt.accommodations.map((acc, i) => (
                <View key={i} style={styles.accRow}>
                  <Text style={styles.accName}>{acc.acommodation?.acommodationName || 'Alojamiento a definir'} : {acc.accomodationDescription}</Text>
                </View>
              ))}
            </>
          )}
          
          <View style={{marginTop: 15, flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'flex-end'}}>
             <Text style={{fontWeight: 'bold', fontSize: 13}}>Total por Persona: U$S {((opt.finalPrice || 0) / (budget.numberPassenger || 1)).toFixed(2)}</Text>
          </View>
        </View>
      ))}
    </Page>

    {/* PÁGINA 2: DETALLES VISUALES (Solo si hay alojamientos) */}
    <Page size="A4" style={styles.page} hyphenationCallback={noHyphenationCallback}>
      <Header />
      
      <Text style={[styles.optionHeader, { textAlign: 'center', marginBottom: 20 }]}>DETALLES DE ALOJAMIENTO</Text>
      
      {budget.budgetOptions?.map((opt) => 
        opt.accommodations?.map((acc, i) => (
          <View key={`${opt.id}-${i}`} style={styles.hotelGalleryItem}>
            <Text style={[styles.accName, { fontSize: 14, marginBottom: 5, color: '#e67e22' }]}>
              {acc.acommodation?.acommodationName || 'Alojamiento a definir'}
            </Text>
            
            {acc.acommodation?.imageUrl1 && (
              <Image src={acc.acommodation.imageUrl1} style={styles.hotelImage} />
            )}
            {acc.acommodation?.imageUrl2 && (
              <Image src={acc.acommodation.imageUrl2} style={styles.hotelImage} />
            )}
            
            {acc.accomodationDescription && (
              <Text style={styles.includeItem}>{acc.accomodationDescription}</Text>
            )}
          </View>
        ))
      )}
      
      <View style={{ marginTop: 30, marginBottom: 50, marginHorizontal: 40 }}>
        <Text style={{ fontSize: 10, fontWeight: 'bold', color: '#333', textAlign: 'justify' }}>{budget.quoteDisclaimer}</Text>
      </View>
    </Page>
  </Document>
  );
};

export default BudgetDocument;
